import React from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import { useTheme } from 'styled-components';
import { AccordionIndicator } from '../../accordion-indicator/accordion-indicator';

interface FaqItemProps {
    id: string;
    question: string;
    answer: string;
    imageUrl?: string;
    isOpen?: boolean;
    onClick?: () => void;
}

export const FaqItem: React.FC<FaqItemProps> = ({ question, answer, imageUrl, isOpen = false, onClick, id }) => {
    const theme = useTheme();
    const reducedMotion = useReducedMotion();

    return (
        <div
            className="scroll-mt-[10.4rem] overflow-hidden rounded-[1.6rem] border border-[#d9cdc5] bg-transparent"
            id={id}
        >
            <h3>
                <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`${id}-answer`}
                    id={`${id}-question`}
                    className={`flex w-full items-center justify-between gap-[1.6rem] p-[2rem] text-left text-[1.6rem] font-bold leading-[2.4rem] transition-colors hover:text-web-copy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-brand-secondary md:px-[3.2rem] md:py-[2.8rem] md:text-[2rem] md:leading-[3rem] ${
                        isOpen ? 'text-web-copy' : 'text-brand-secondary'
                    }`}
                    onClick={onClick}
                >
                    {question}
                    <AccordionIndicator open={isOpen} />
                </button>
            </h3>
            <motion.div
                initial={false}
                animate={{
                    height: isOpen ? 'auto' : 0,
                    opacity: isOpen ? 1 : 0,
                    visibility: isOpen ? 'visible' : 'hidden',
                }}
                transition={{
                    duration: reducedMotion ? 0 : 0.3,
                    ease: [0.22, 1, 0.36, 1],
                    visibility: { delay: isOpen || reducedMotion ? 0 : 0.3 },
                }}
                aria-hidden={!isOpen}
                id={`${id}-answer`}
                role="region"
                aria-labelledby={`${id}-question`}
                className="overflow-hidden [&_a]:text-[var(--faq-link-color)] [&_a]:underline [&_a:hover]:text-[var(--faq-link-hover-color)] [&_a:hover]:no-underline"
                style={{
                    ['--faq-link-color' as string]: theme.palette.color.primary.main,
                    ['--faq-link-hover-color' as string]: theme.palette.color.primary.light,
                }}
            >
                <div className="px-[2rem] pb-[2.4rem] md:px-[3.2rem] md:pb-[3.2rem] text-[1.4rem] leading-[2.2rem] text-web-copy md:text-[1.6rem] md:leading-[2.4rem]">
                    <div dangerouslySetInnerHTML={{ __html: answer }} />
                    {imageUrl && (
                        <div>
                            <Image
                                src={imageUrl}
                                alt={`${question} image`}
                                layout="responsive"
                                width="100%"
                                height="50%"
                                objectFit="contain"
                            />
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};
